<template>
  <InputFrame
    :title="
      type === PSCType.Kaizen
        ? t(`views.psc.CreateKaizen.title`)
        : t(`views.psc.Create.createPSCHeadline`)
    "
    :display-mode="showInModal ? InputFrameMode.modal : undefined"
    create
    :loading="loading"
    :submit="
      async () => {
        await createPSC();
        return { success: true };
      }
    "
    @cancel="emit('update:visible-action-modal', false)"
  >
    <InputRow
      :model-value="psc.teamLeadId"
      :display-name="t('views.psc.Create.teamLeader')"
      class="doNotPrintMe"
      name="teamLeadId"
      required
    >
      <template #default="{ field: inputField }">
        <SearchSelect
          v-bind="inputField"
          v-model="psc.teamLeadId"
          name="teamLead"
          :mode="SelectMode.USER"
          data-testid="teamLead"
          :placeholder="t('views.psc.Create.teamLeaderPlaceholder')"
          @update:model-value="removeFromMembers"
        />
      </template>
    </InputRow>
    <InputRow
      :model-value="psc.teamMembers"
      :display-name="t('views.psc.Create.teamMembers')"
      class="doNotPrintMe"
      name="teamMembers"
    >
      <template #default="{ field: inputField }">
        <SearchSelect
          v-bind="inputField"
          v-model="psc.teamMembers"
          :mode="SelectMode.USER"
          name="teamMembers"
          :multiple="true"
          :placeholder="t('views.psc.Create.memberPlaceholder')"
          :exclude="psc.teamLeadId ? [psc.teamLeadId] : []"
        />
      </template>
    </InputRow>
    <PSCComponent v-model:psc="psc" />
    <template #leading>
      <div class="flex justify-content-betwee gap-2 mr-2">
        <LISAButton class="p-button-info" :disabled="showInModal" @click="printMe">
          <i class="fas fa-print pr-1" />{{ t("views.psc.Edit.print") }}
          <InfoTooltip v-if="showInModal" :text="t('views.psc.Edit.printDenied')" />
        </LISAButton>
      </div>
    </template>
  </InputFrame>
</template>

<script setup lang="ts">
import { useTranslation } from "@/language/translation";

import InputFrame from "@/components/shared/form/InputFrame.vue";
import InputRow from "@/components/shared/form/InputRow.vue";
import { ActionType, EventType, PSCType, usePSCCreateMutation } from "@/graphql";
import { computed, ref, watch, type PropType } from "vue";
import PSCComponent from "../components/PSCComponent.vue";
import SearchSelect from "@/components/shared/searchSelects/SearchSelect.vue";
import { SelectMode } from "@/components/shared/searchSelects/SelectDefinitions";
import { useRouter } from "vue-router";
import LISAButton from "@/components/shared/primevue/LISAButton.vue";
import { InputFrameMode } from "@/components/shared/form/InputFrameMode";
import InfoTooltip from "@/components/shared/InfoTooltip.vue";
import { getDefaultPSC } from "../helper/PSCAreaConfig";
import { allowNextNavigation } from "@/functions/useUnsavedChanges";
import { useSQDCGlobalSettings } from "@/modules/sqdc/helper/useSQDCGlobalSettings";

const props = defineProps({
  type: {
    type: String as PropType<PSCType>,
    required: true,
  },
  eventId: {
    type: Number,
    required: false,
    default: undefined,
  },
  eventType: {
    type: String as PropType<EventType>,
    required: false,
    default: undefined,
  },
  showInModal: {
    type: Boolean,
    required: false,
    default: false,
  },
});

const { actionOrdering } = useSQDCGlobalSettings();

const psc = ref(getDefaultPSC(props.type));

watch(
  computed(() => props.type),
  () => {
    psc.value = getDefaultPSC(props.type);
  },
);

function removeFromMembers(leaderId?: number) {
  if (leaderId == null) {
    return;
  }
  const toRemove = psc.value.teamMembers.findIndex((member) => member === leaderId);
  if (toRemove != null && toRemove !== -1) {
    psc.value.teamMembers.splice(toRemove, 1);
  }
}

const router = useRouter();
const emit = defineEmits<{
  "update:visible-action-modal": [visible: boolean];
}>();

const { mutate: createPSCMutation, loading, onDone } = usePSCCreateMutation({});

async function createPSC() {
  await createPSCMutation(
    {
      psc: {
        closed: psc.value.closed,
        type: psc.value.type,
        problem: psc.value.problem ?? "",
        hashtags: psc.value.hashtags,
        siteId: psc.value.siteId,
        teamLeadId: psc.value.teamLeadId!,
        teamMembers: psc.value.teamMembers,
        Area1: {
          detectedOn: psc.value.Area1.detectedOn,
          issue: psc.value.Area1.issue,
          discoveredFrom: psc.value.Area1.discoveredFrom ?? "",
          fileIds: psc.value.Area1.fileIds,
          area: psc.value.Area1.area,
          defects: psc.value.Area1.defects,
          goal: psc.value.Area1.goal,
          plant: psc.value.Area1.plant,
          product: psc.value.Area1.product,
        },
        Area1Kaizen: psc.value.Area1K,
        Area2: psc.value.Area2,
        Area3: {
          tasks: psc.value.Area3.tasks.map((task) => ({
            assigneeId: task.assigneeId!,
            deadline: task.deadline!,
            description: task.description,
            status: task.status!,
          })),
        },
        Area3Kaizen: psc.value.Area3K,
        Area4: {
          fileIds: psc.value.Area4.fileIds,
          reasons: psc.value.Area4.reasons.map((reason) => ({
            selected: reason.selected ?? 0,
            name: reason.name,
            why0: reason.why0,
            why1: reason.why1,
            why2: reason.why2,
            why3: reason.why3,
            why4: reason.why4,
          })),
        },
        Area4Kaizen: psc.value.Area4K,
        Area5: {
          tasks: psc.value.Area5.tasks.map((task) => ({
            assigneeId: task.assigneeId!,
            deadline: task.deadline!,
            description: task.description,
            status: task.status!,
          })),
        },
        Area6: psc.value.Area6,
        Area6Kaizen: {
          ...psc.value.Area6K,
          datapoints: psc.value.Area6K.datapoints ?? [],
        },
        Area7: {
          tasks: psc.value.Area7.tasks.map((task) => ({
            assigneeId: task.assigneeId!,
            deadline: task.deadline!,
            description: task.description,
            status: task.status!,
          })),
        },
        Area8: {
          tasks: psc.value.Area8.tasks.map((task) => ({
            assigneeId: task.assigneeId!,
            deadline: task.deadline!,
            description: task.description,
            status: task.status!,
          })),
        },
        Area9: psc.value.Area9,
      },
      eventId: props.eventId,
      eventType: props.eventType,
    },
    {
      update: (cache, { data }) => {
        const createdPSC = data?.PSCCreate;
        if (props.eventId == null || props.eventType == null || createdPSC == null) {
          return;
        }

        const storedObject = cache.identify({
          __typename: ActionType.PSC,
          id: createdPSC.id,
        });
        cache.modify({
          id: storedObject,
          fields: {
            assignee() {
              return createdPSC.TeamLead!;
            },
            status() {
              return createdPSC.closed ? 4 : 1;
            },
            deadline() {
              return createdPSC.createdAt;
            },
          },
        });

        const eventCacheId = cache.identify({
          __typename: props.eventType,
          id: props.eventId,
        });

        cache.modify({
          id: eventCacheId,
          fields: {
            actions(existingRefs = [], { toReference }) {
              const pscRef = toReference({ __typename: "PSC", id: createdPSC.id });
              if (actionOrdering.value === "ASC") {
                return [...existingRefs, pscRef];
              }
              return [pscRef, ...existingRefs];
            },
          },
          broadcast: true,
          optimistic: true,
        });
      },
    },
  );
}

onDone(() => {
  if (!props.showInModal) {
    allowNextNavigation();
    router.push({
      name: "PSCOverview",
      params: { type: psc.value.type, archive: psc.value.closed.toString() },
    });
  } else {
    emit("update:visible-action-modal", false);
  }
});

function printMe() {
  window.print();
}

const { t } = useTranslation();
</script>

<style lang="scss" scoped></style>
